import { useEffect, useRef, useState } from "react";
import Layout from "@/components/Layout";
import { Button, Card, Empty, Spinner, fmtNum } from "@/components/ui/Primitives";
import { api, formatErr } from "@/lib/api";
import {
  QrCode, CheckCircle2, AlertCircle, Trash2, X, Recycle, TrendingDown, Send,
} from "lucide-react";

const brl = (n) =>
  Number(n || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const CAT_LABEL = {
  cx1: "CX1", cx2: "CX2", cx3: "CX3", cx3g: "CX3G", cx4: "CX4", granel: "GRL",
};

export default function Rebeneficiamento() {
  const [q, setQ] = useState("");
  // scans: [{ ...pallet, quantidade_depois, obs }]
  const [scans, setScans] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [list, setList] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [filterForn, setFilterForn] = useState("");
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const loadList = async () => {
    setLoadingList(true);
    try {
      const params = {};
      if (filterFrom) params.from_ = filterFrom;
      if (filterTo) params.to = filterTo;
      if (filterForn) params.fornecedor = filterForn;
      const { data } = await api.get("/rebeneficiamentos", { params });
      setList(data);
    } catch (e) {
      setList({ items: [], total_perda_kg: 0, total_perda_sacos: 0, total_prejuizo: 0 });
    } finally {
      setLoadingList(false);
    }
  };
  useEffect(() => { loadList(); /* eslint-disable-next-line */ }, []);

  const showMsg = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), 4000);
  };

  const scan = async (e) => {
    e.preventDefault();
    const val = q.trim();
    if (!val) return;
    setBusy(true);
    try {
      const { data: pl } = await api.get("/rebeneficiamentos/resolve-pallet", { params: { q: val } });
      if (scans.some((s) => s.id === pl.id)) {
        showMsg("warn", `${pl.numero} já está na lista.`);
      } else {
        // Adiciona com "quantidade_depois" default = quantidade atual (nada retirado ainda)
        const antes = Number(pl.restante ?? pl.total_caixas ?? 0);
        setScans((prev) => [{ ...pl, quantidade_depois: String(antes), obs: "" }, ...prev]);
        showMsg("ok", `${pl.numero} adicionado à lista.`);
      }
    } catch (e2) {
      showMsg("err", formatErr(e2));
    } finally {
      setQ("");
      setBusy(false);
      inputRef.current?.focus();
    }
  };

  const updateScan = (id, field, value) => {
    setScans((prev) => prev.map((s) => (s.id === id ? { ...s, [field]: value } : s)));
  };

  const remove = (id) => {
    setScans((prev) => prev.filter((s) => s.id !== id));
  };

  const clearAll = () => {
    if (scans.length === 0 || window.confirm("Descartar todos os pallets bipados?")) setScans([]);
  };

  // Calcula perda/prejuízo para uma linha
  const computeRow = (s) => {
    const it0 = (s.itens || [])[0] || {};
    const isGranel = !!s.granel || it0.categoria === "granel";
    const antes = Number(s.restante ?? s.total_caixas ?? 0);
    const depois = Number(s.quantidade_depois || 0);
    const perda = Math.max(0, antes - depois);
    const custoUnit = Number((s.custo_info || {}).custo_unit || 0);
    const prejuizo = perda * custoUnit;
    const perdaKg = isGranel ? perda : perda * 20;
    return { isGranel, antes, depois, perda, perdaKg, custoUnit, prejuizo, it0 };
  };

  const totais = scans.reduce(
    (acc, s) => {
      const r = computeRow(s);
      acc.perdaKg += r.perdaKg;
      acc.perdaSacos += r.isGranel ? 0 : r.perda;
      acc.prejuizo += r.prejuizo;
      return acc;
    },
    { perdaKg: 0, perdaSacos: 0, prejuizo: 0 }
  );

  const confirmarTodos = async () => {
    if (scans.length === 0) return;
    // Valida antes de enviar
    for (const s of scans) {
      const r = computeRow(s);
      if (isNaN(r.depois) || r.depois < 0) {
        showMsg("err", `${s.numero}: quantidade depois inválida`);
        return;
      }
      if (r.depois > r.antes + 0.001) {
        showMsg("err", `${s.numero}: depois (${r.depois}) maior que antes (${r.antes})`);
        return;
      }
    }
    setBusy(true);
    try {
      const payload = {
        itens: scans.map((s) => ({
          pallet_id: s.id,
          quantidade_depois: Number(s.quantidade_depois || 0),
          observacoes: s.obs || "",
        })),
      };
      const { data } = await api.post("/rebeneficiamentos/batch", payload);
      showMsg(
        "ok",
        `${data.itens.length} pallets rebeneficiados · perda total ${fmtNum(data.total_perda_kg, 1)} kg = R$ ${brl(data.total_prejuizo)}`
      );
      setScans([]);
      loadList();
    } catch (e) {
      showMsg("err", formatErr(e));
    } finally {
      setBusy(false);
      inputRef.current?.focus();
    }
  };

  const desfazer = async (rid) => {
    if (!window.confirm("Desfazer este rebeneficiamento? O pallet original voltará ao estoque e o novo será removido.")) return;
    try {
      await api.delete(`/rebeneficiamentos/${rid}`);
      showMsg("ok", "Rebeneficiamento desfeito.");
      loadList();
    } catch (e) {
      showMsg("err", formatErr(e));
    }
  };

  return (
    <Layout
      title="Rebeneficiamento de Pallets"
      subtitle="Bipe os pallets a rebeneficiar, informe o que sobrou, e confirme tudo de uma vez"
      actions={
        <div className="flex gap-2">
          {scans.length > 0 && (
            <Button variant="secondary" onClick={clearAll} data-testid="rebenef-clear-all">
              <Trash2 size={16} /> Limpar ({scans.length})
            </Button>
          )}
          <Button
            onClick={confirmarTodos}
            disabled={busy || scans.length === 0}
            data-testid="rebenef-confirmar-todos-btn"
          >
            {busy ? <Spinner size={16} /> : <><Send size={16} /> Confirmar Todos ({scans.length})</>}
          </Button>
        </div>
      }
    >
      {/* Área de bipagem */}
      <Card className="p-5 mb-5 bg-orange-50/50 border-orange-200">
        <form onSubmit={scan} className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-orange-500 text-white flex items-center justify-center flex-shrink-0">
            <QrCode size={24} />
          </div>
          <div className="flex-1">
            <label className="block text-xs font-bold uppercase tracking-[0.1em] text-orange-700 mb-1">
              Bipe o QR do pallet a rebeneficiar <span className="normal-case font-normal text-orange-600 opacity-80">— ou digite só o número da etiqueta (ex.: 3312)</span>
            </label>
            <input
              ref={inputRef}
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Bipe o QR ou digite o nº da etiqueta (ex: 3312 · P-3312 · uuid)"
              className="w-full rounded-md bg-white border border-orange-300 px-3 py-3 text-base font-mono focus:outline-none focus:ring-2 focus:ring-orange-500"
              autoComplete="off"
              data-testid="rebenef-scan-input"
              disabled={busy}
            />
          </div>
          <Button type="submit" disabled={busy || !q.trim()} data-testid="rebenef-scan-btn">
            {busy ? <Spinner size={16} /> : <>Bipar</>}
          </Button>
        </form>
        {msg && (
          <div
            className={`mt-3 px-3 py-2 rounded-md text-sm flex items-center gap-2 ${
              msg.type === "ok"
                ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                : msg.type === "warn"
                ? "bg-orange-100 text-orange-800 border border-orange-300"
                : "bg-red-100 text-danger border border-red-300"
            }`}
            data-testid="rebenef-msg"
          >
            {msg.type === "ok" ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
            {msg.text}
          </div>
        )}
      </Card>

      {/* Lista de pallets bipados com input inline */}
      {scans.length > 0 && (
        <Card className="p-0 overflow-hidden mb-5 border-2 border-brand-500">
          <div className="px-4 py-2.5 bg-brand-500/10 border-b border-brand-500/30 flex items-center justify-between">
            <div className="text-xs font-bold uppercase tracking-[0.1em] text-brand-700">
              Pallets a rebeneficiar ({scans.length})
            </div>
            <div className="flex items-center gap-4 text-xs">
              <span className="text-orange-700 font-mono">
                Perda: <strong>{fmtNum(totais.perdaKg, 1)} kg</strong>
                {totais.perdaSacos > 0 && <> · <strong>{fmtNum(totais.perdaSacos, 0)} sc</strong></>}
              </span>
              <span className="text-red-700 font-mono">
                Prejuízo: <strong>R$ {brl(totais.prejuizo)}</strong>
              </span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-ink-50/70 text-[11px] uppercase tracking-wider text-ink-500">
                  <th className="px-3 py-2 text-left">Pallet</th>
                  <th className="px-3 py-2 text-left">NF · Fornecedor</th>
                  <th className="px-3 py-2 text-center">Cat.</th>
                  <th className="px-3 py-2 text-right">Antes</th>
                  <th className="px-3 py-2 text-right">Depois</th>
                  <th className="px-3 py-2 text-right">Perda</th>
                  <th className="px-3 py-2 text-right">Prejuízo</th>
                  <th className="px-3 py-2 text-left">Obs</th>
                  <th className="px-3 py-2 w-8"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-200">
                {scans.map((s) => {
                  const r = computeRow(s);
                  return (
                    <tr key={s.id} className="hover:bg-ink-50/40" data-testid={`scanned-row-${s.numero}`}>
                      <td className="px-3 py-2">
                        <div className="font-mono font-bold text-ink-900">{s.numero}</div>
                        <div className="text-[10px] text-ink-500">{r.it0.tipo_cebola || ""}</div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium">#{r.it0.nota_numero || s.nota_numero || "?"}</div>
                        <div className="text-[10px] text-ink-500 truncate max-w-[180px]">
                          {r.it0.fornecedor || s.fornecedor || "—"}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-brand-500/10 text-brand-700 border border-brand-500/30">
                          {r.isGranel ? "GRL" : CAT_LABEL[r.it0.categoria] || "?"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right font-mono">
                        {fmtNum(r.antes, r.isGranel ? 2 : 0)}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <input
                          type="number"
                          step={r.isGranel ? "0.01" : "1"}
                          value={s.quantidade_depois}
                          onChange={(e) => updateScan(s.id, "quantidade_depois", e.target.value)}
                          className="w-20 text-right font-mono font-semibold border border-brand-500 rounded px-1.5 py-1 text-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500"
                          data-testid={`row-depois-${s.numero}`}
                        />
                        <div className="text-[9px] text-ink-400 mt-0.5">
                          {r.isGranel ? "kg" : "sc"}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-orange-700 font-semibold">
                        {r.perda > 0 ? (
                          <>
                            {fmtNum(r.perda, r.isGranel ? 2 : 0)} {r.isGranel ? "kg" : "sc"}
                            {!r.isGranel && (
                              <div className="text-[9px] text-orange-600 opacity-70">
                                ≈ {fmtNum(r.perdaKg, 1)} kg
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="text-ink-300">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-red-700 font-bold">
                        {r.prejuizo > 0 ? `R$ ${brl(r.prejuizo)}` : <span className="text-ink-300 font-normal">—</span>}
                        <div className="text-[9px] text-ink-400 font-normal">
                          R$ {brl(r.custoUnit)}/{r.isGranel ? "kg" : "sc"}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={s.obs}
                          onChange={(e) => updateScan(s.id, "obs", e.target.value)}
                          placeholder="Ex.: brotada"
                          className="w-full text-xs border border-ink-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-brand-500"
                          data-testid={`row-obs-${s.numero}`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <button
                          onClick={() => remove(s.id)}
                          className="text-ink-400 hover:text-danger p-1"
                          title="Remover"
                          data-testid={`row-remove-${s.numero}`}
                        >
                          <X size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-ink-50 border-t-2 border-ink-300 font-bold">
                  <td className="px-3 py-2" colSpan={5}>Total ({scans.length} pallets)</td>
                  <td className="px-3 py-2 text-right font-mono text-orange-700">
                    {fmtNum(totais.perdaKg, 1)} kg
                    {totais.perdaSacos > 0 && (
                      <div className="text-[10px] font-normal">
                        {fmtNum(totais.perdaSacos, 0)} sacos
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-red-700">
                    R$ {brl(totais.prejuizo)}
                  </td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>
      )}

      {/* Totalizadores histórico */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
        <Card className="p-4">
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">Registros no período</div>
          <div className="text-3xl font-mono font-bold text-ink-900 mt-1" data-testid="tot-registros">
            {list?.items?.length || 0}
          </div>
        </Card>
        <Card className="p-4 bg-orange-50/40 border-orange-200">
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-orange-700">Perda total</div>
          <div className="text-3xl font-mono font-bold text-orange-800 mt-1" data-testid="tot-perda">
            {fmtNum(list?.total_perda_kg || 0, 1)} <span className="text-base">kg</span>
          </div>
          {list?.total_perda_sacos > 0 && (
            <div className="text-xs text-orange-700 mt-0.5">
              {fmtNum(list.total_perda_sacos, 0)} sacos
            </div>
          )}
        </Card>
        <Card className="p-4 bg-red-50/50 border-red-200">
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-red-700 flex items-center gap-1">
            <TrendingDown size={13} /> Prejuízo total
          </div>
          <div className="text-3xl font-mono font-bold text-red-800 mt-1" data-testid="tot-prejuizo">
            R$ {brl(list?.total_prejuizo || 0)}
          </div>
        </Card>
      </div>

      <Card className="p-4 mb-5">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-ink-500 font-bold block">De</label>
            <input type="date" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)}
              className="rounded-md border border-ink-300 px-2 py-1.5 text-sm" data-testid="filter-from" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-ink-500 font-bold block">Até</label>
            <input type="date" value={filterTo} onChange={(e) => setFilterTo(e.target.value)}
              className="rounded-md border border-ink-300 px-2 py-1.5 text-sm" data-testid="filter-to" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="text-[10px] uppercase tracking-wider text-ink-500 font-bold block">Fornecedor</label>
            <input type="text" value={filterForn} onChange={(e) => setFilterForn(e.target.value)}
              placeholder="Filtro por nome do produtor..."
              className="w-full rounded-md border border-ink-300 px-2 py-1.5 text-sm" data-testid="filter-forn" />
          </div>
          <Button onClick={loadList} data-testid="filter-apply-btn">Aplicar Filtros</Button>
        </div>
      </Card>

      {/* Histórico */}
      <Card className="p-0 overflow-hidden">
        <div className="px-5 py-3 border-b border-ink-200 bg-ink-50/70 flex items-center justify-between">
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">
            Histórico de Rebeneficiamentos
          </div>
        </div>
        {loadingList ? (
          <div className="p-8 flex justify-center"><Spinner size={24} /></div>
        ) : !list || list.items.length === 0 ? (
          <div className="p-8">
            <Empty title="Sem registros" hint="Bipe pallets acima e confirme para começar." />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-ink-50/70 text-[11px] uppercase tracking-wider text-ink-500">
                  <th className="px-3 py-2 text-left">Data</th>
                  <th className="px-3 py-2 text-left">Pallet Orig.</th>
                  <th className="px-3 py-2 text-left">Novo Pallet</th>
                  <th className="px-3 py-2 text-left">NF · Fornecedor</th>
                  <th className="px-3 py-2 text-center">Cat.</th>
                  <th className="px-3 py-2 text-right">Antes</th>
                  <th className="px-3 py-2 text-right">Depois</th>
                  <th className="px-3 py-2 text-right">Perda (kg)</th>
                  <th className="px-3 py-2 text-right">Prejuízo</th>
                  <th className="px-3 py-2 text-left">Obs</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-200">
                {list.items.map((r) => (
                  <tr key={r.id} className="hover:bg-ink-50/40" data-testid={`row-${r.id}`}>
                    <td className="px-3 py-2 whitespace-nowrap">{(r.data || "").split("-").reverse().join("/")}</td>
                    <td className="px-3 py-2 font-mono font-semibold">{r.pallet_numero_original}</td>
                    <td className="px-3 py-2 font-mono">{r.pallet_numero_novo || <span className="text-ink-400">—</span>}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium">#{r.nota_numero || "—"}</div>
                      <div className="text-xs text-ink-500">{r.fornecedor || "—"}</div>
                    </td>
                    <td className="px-3 py-2 text-center text-xs">
                      {r.categoria ? (r.categoria === "granel" ? "GRL" : CAT_LABEL[r.categoria] || r.categoria) : "—"}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {fmtNum(r.quantidade_antes || 0, r.unidade_medida === "kg" ? 1 : 0)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {fmtNum(r.quantidade_depois || 0, r.unidade_medida === "kg" ? 1 : 0)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-orange-700 font-semibold">
                      {fmtNum(r.perda_kg || 0, 1)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-red-700 font-bold">
                      R$ {brl(r.prejuizo_total || 0)}
                    </td>
                    <td className="px-3 py-2 text-xs text-ink-500 max-w-[200px] truncate" title={r.observacoes}>
                      {r.observacoes || ""}
                    </td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => desfazer(r.id)}
                        className="text-ink-400 hover:text-danger p-1"
                        title="Desfazer (restaura o pallet original)"
                        data-testid={`undo-${r.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-ink-50 font-bold border-t-2 border-ink-300">
                  <td className="px-3 py-2" colSpan={7}>Total do período</td>
                  <td className="px-3 py-2 text-right font-mono text-orange-700">
                    {fmtNum(list.total_perda_kg || 0, 1)} kg
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-red-700">
                    R$ {brl(list.total_prejuizo || 0)}
                  </td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>
    </Layout>
  );
}
